import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Power, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type {
  BenefitsCategory,
  BenefitsPlan,
  BenefitsPlanDetail,
} from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const CATEGORIES: BenefitsCategory[] = [
  "medical",
  "dental",
  "vision",
  "life",
  "disability",
  "wellness",
  "retirement",
];

// Default 4 KSA majors per Q2 scope lock. Customers extend via free-form
// provider_name; this list is just the UI dropdown convenience.
const DEFAULT_PROVIDERS = [
  "Tawuniya",
  "Bupa Arabia",
  "MedGulf",
  "Walaa",
] as const;

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function fmtWindow(plan: BenefitsPlan): string {
  return (
    `${MONTH_NAMES[plan.annual_window_open_month - 1]} ` +
    `${plan.annual_window_open_day} → ` +
    `${MONTH_NAMES[plan.annual_window_close_month - 1]} ` +
    `${plan.annual_window_close_day}`
  );
}

function NewBenefitsPlanDialog({
  legalEntityId,
  initialCategory,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  initialCategory: BenefitsCategory;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    plan_code: "",
    plan_name: "",
    plan_name_ar: "",
    category: initialCategory,
    provider_name: DEFAULT_PROVIDERS[0] as string,
    provider_contract_number: "",
    cchi_compliant: initialCategory === "medical",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createBenefitsPlan({
        legal_entity_id: legalEntityId,
        category: form.category,
        plan_code: form.plan_code,
        plan_name: form.plan_name,
        plan_name_ar: form.plan_name_ar,
        provider_name: form.provider_name,
        provider_contract_number: form.provider_contract_number || null,
        cchi_compliant: form.cchi_compliant,
        tiers: [],
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.detail : String(e));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.benefits_plans_new", "Add a benefits plan")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={
              !form.plan_code || !form.plan_name || !form.plan_name_ar
            }
            className="rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("common.create", "Create")}
          </button>
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("policies.benefits_category", "Category")}
          </label>
          <select
            value={form.category}
            onChange={(e) =>
              setForm({
                ...form,
                category: e.target.value as BenefitsCategory,
                cchi_compliant: e.target.value === "medical",
              })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("policies.benefits_plan_code", "Plan code (snake_case)")}
            </label>
            <input
              value={form.plan_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  plan_code: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, "_"),
                })
              }
              placeholder="std_medical"
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("policies.benefits_provider", "Provider")}
            </label>
            <input
              value={form.provider_name}
              onChange={(e) =>
                setForm({ ...form, provider_name: e.target.value })
              }
              placeholder="Tawuniya"
              list="benefits-provider-suggestions"
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
            <datalist id="benefits-provider-suggestions">
              {DEFAULT_PROVIDERS.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("policies.benefits_plan_name", "Name (EN)")}
            </label>
            <input
              value={form.plan_name}
              onChange={(e) =>
                setForm({ ...form, plan_name: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("policies.benefits_plan_name_ar", "Name (AR)")}
            </label>
            <input
              dir="rtl"
              value={form.plan_name_ar}
              onChange={(e) =>
                setForm({ ...form, plan_name_ar: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t(
              "policies.benefits_contract_number",
              "Provider contract # (optional)",
            )}
          </label>
          <input
            value={form.provider_contract_number}
            onChange={(e) =>
              setForm({ ...form, provider_contract_number: e.target.value })
            }
            placeholder="TWN-2026-RIY-001"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          />
        </div>
        {form.category === "medical" && (
          <label className="flex items-center gap-2 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={form.cchi_compliant}
              onChange={(e) =>
                setForm({ ...form, cchi_compliant: e.target.checked })
              }
            />
            {t(
              "policies.benefits_cchi_compliant",
              "Plan satisfies CCHI minimum coverage",
            )}
          </label>
        )}
        {error && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function PlanCard({
  plan,
  detail,
  onDeactivate,
}: {
  plan: BenefitsPlan;
  detail: BenefitsPlanDetail | undefined;
  onDeactivate: () => void;
}) {
  const { t } = useTranslation();
  const tiers = detail?.tiers ?? [];
  const activeTiers = tiers.filter((tier) => tier.is_active);
  return (
    <div
      className={`rounded-2xl border p-4 ${
        plan.is_active
          ? "border-border-subtle bg-white"
          : "border-rose-200 bg-rose-50/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-text-primary">
              {plan.plan_name}
            </h3>
            {plan.category === "medical" && plan.cchi_compliant && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                <ShieldCheck className="h-3 w-3" />
                CCHI
              </span>
            )}
            {!plan.is_active && (
              <Badge tone="slate">
                {t("policies.benefits_inactive", "Inactive")}
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-text-tertiary" dir="rtl">
            {plan.plan_name_ar}
          </div>
        </div>
        {plan.is_active && (
          <button
            type="button"
            onClick={onDeactivate}
            className="rounded-lg border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
            title={t("policies.benefits_deactivate", "Deactivate")}
          >
            <Power className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">
            {t("policies.benefits_provider", "Provider")}
          </dt>
          <dd className="text-text-primary">{plan.provider_name}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("policies.benefits_plan_code", "Code")}
          </dt>
          <dd className="font-mono text-text-primary">{plan.plan_code}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("policies.benefits_enrollment_window", "Annual window")}
          </dt>
          <dd className="text-text-primary">{fmtWindow(plan)}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("policies.benefits_new_hire_window", "New-hire window")}
          </dt>
          <dd className="text-text-primary">
            {plan.new_hire_window_days}{" "}
            {t("common.days", "days")}
          </dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("policies.benefits_dependents", "Dependents")}
          </dt>
          <dd className="text-text-primary">
            {[
              plan.dependent_spouse_included && "Spouse",
              plan.dependent_children_included && "Children",
              plan.dependent_parents_included && "Parents",
            ]
              .filter(Boolean)
              .join(" · ") || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("policies.benefits_auto_renew", "Auto-renew")}
          </dt>
          <dd className="text-text-primary">
            {plan.auto_renew ? t("common.yes", "Yes") : t("common.no", "No")}
          </dd>
        </div>
      </dl>
      {activeTiers.length > 0 && (
        <div className="mt-3 border-t border-border-subtle pt-3">
          <div className="text-xs font-medium text-text-secondary">
            {t("policies.benefits_tiers", "Tiers")} · {activeTiers.length}
          </div>
          <ul className="mt-1 space-y-0.5">
            {activeTiers.map((tier) => (
              <li
                key={tier.id}
                className="flex items-center justify-between text-xs text-text-secondary"
              >
                <span>
                  #{tier.tier_order} · {tier.tier_name}
                </span>
                <span className="font-mono text-text-tertiary">
                  {tier.employer_contribution_pct}% emp /{" "}
                  {tier.employee_contribution_pct}% ee
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function BenefitsPlansPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [activeCategory, setActiveCategory] = useState<BenefitsCategory>(
    "medical",
  );
  const [showCreate, setShowCreate] = useState(false);

  const list = useQuery({
    queryKey: ["benefits-plans", legalEntityId, activeCategory],
    queryFn: () =>
      api.listBenefitsPlans(legalEntityId ?? "", {
        category: activeCategory,
        includeInactive: true,
      }),
    enabled: !!legalEntityId,
  });

  // Pull detail (with tiers) for each plan so cards can render the tier list.
  const detailIds = useMemo(
    () => (list.data ?? []).map((p) => p.id),
    [list.data],
  );
  const detailQuery = useQuery({
    queryKey: ["benefits-plan-details", detailIds],
    queryFn: () =>
      Promise.all(detailIds.map((id) => api.getBenefitsPlan(id))),
    enabled: detailIds.length > 0,
  });
  const detailByPlan = useMemo(() => {
    const m = new Map<string, BenefitsPlanDetail>();
    (detailQuery.data ?? []).forEach((d) => m.set(d.id, d));
    return m;
  }, [detailQuery.data]);

  const deactivate = useMutation({
    mutationFn: (id: string) => api.deactivateBenefitsPlan(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["benefits-plans"] });
    },
  });

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("policies.benefits_plans_title", "Benefits plans")}
        subtitle={t(
          "policies.benefits_plans_subtitle",
          "Per-legal-entity catalog covering the 7 benefit categories. Provider integration is record-only in v1.",
        )}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {t("policies.benefits_plans_new", "Add a benefits plan")}
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border-subtle">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setActiveCategory(c)}
            className={`-mb-px rounded-t-lg border-b-2 px-3 py-1.5 text-sm capitalize ${
              activeCategory === c
                ? "border-brand-blue text-brand-deep"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {t(`policies.benefits_cat_${c}`, c)}
          </button>
        ))}
      </div>

      {list.isLoading && <Loading />}
      {list.isError && (
        <ErrorPanel message={(list.error as Error).message} />
      )}
      {list.data && list.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-8 text-center text-sm text-text-tertiary">
          {t(
            "policies.benefits_plans_empty",
            "No plans in this category yet. Add one to get started.",
          )}
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              detail={detailByPlan.get(plan.id)}
              onDeactivate={() => deactivate.mutate(plan.id)}
            />
          ))}
        </div>
      )}

      {showCreate && legalEntityId && (
        <NewBenefitsPlanDialog
          legalEntityId={legalEntityId}
          initialCategory={activeCategory}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["benefits-plans"] });
          }}
        />
      )}
    </div>
  );
}
